import { get } from 'lodash';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';

import { Warehouse as WarehouseIcon } from '@mui/icons-material';
import { Autocomplete, CircularProgress, InputAdornment, TextField } from '@mui/material';

import { warehouseApi } from 'src/api/warehouse';
import { setError } from 'src/slices/error';
import { useDispatch } from 'src/store';
import { Warehouse } from 'src/types/warehouse';

interface Props {
    warehouse?: string;
    disabled?: boolean;

    setWarehouse: (warehouse: Warehouse) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}

const WarehouseAutocomplete: FC<Props> = ({ warehouse, disabled, setWarehouse, onKeyDown }) => {
    const dispatch = useDispatch();

    const [selected, setSelected] = useState<Warehouse>();
    const [results, setResults] = useState<Warehouse[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [search, setSearch] = useState<string>('');

    const fetchData = useCallback(async (): Promise<void> => {
        try {
            const results = await warehouseApi.find({ search, select: 'name' });

            setResults(results);
            setLoading(false);
        } catch (error) {
            dispatch(setError(error));
        }
    }, [search, dispatch]);

    const getWarehouse = useCallback(async (): Promise<void> => {
        try {
            setLoading(true);

            const result = await warehouseApi.findById(warehouse, { select: 'name' });

            setSelected(result);
            setLoading(false);
        } catch (error) {
            dispatch(setError(error));
        }
    }, [warehouse, dispatch]);

    useEffect(() => {
        if (warehouse) getWarehouse();
    }, [warehouse, getWarehouse]);

    useEffect(() => {
        if (!loading) return;

        fetchData();
    }, [loading, fetchData]);

    useEffect(() => {
        if (selected) setWarehouse(selected);
        if (selected === null) setWarehouse(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    if (warehouse && !selected)
        return (
            <Autocomplete
                key='1'
                disabled
                fullWidth
                options={[]}
                renderInput={(params): JSX.Element => <TextField fullWidth label='Lager' variant='outlined' {...params} />}
            />
        );

    return (
        <Autocomplete
            key='2'
            fullWidth
            getOptionLabel={(option) => get(option, 'name')}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            loading={loading}
            onChange={(x, value: Warehouse) => {
                if (!value) setSelected(null);
                else setSelected(value);
            }}
            onClose={() => setLoading(false)}
            onOpen={() => setLoading(true)}
            options={results}
            value={selected}
            disabled={disabled}
            renderInput={(params): JSX.Element => (
                <TextField
                    {...(onKeyDown && { onKeyDown })}
                    onChange={(event) => {
                        if (event.target.value) {
                            setSearch(event.target.value);
                            setLoading(true);
                        } else {
                            setSelected(null);
                        }
                    }}
                    fullWidth
                    label='Lager'
                    variant='outlined'
                    {...params}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position='start' sx={{ ml: 1 }}>
                                <WarehouseIcon />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <Fragment>
                                {loading ? <CircularProgress color='primary' size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </Fragment>
                        ),
                    }}
                />
            )}
        />
    );
};

export default WarehouseAutocomplete;
